import React, { useState, useEffect } from 'react';
import sbpService from '../../service/sbp.service';
import backendApiService from '../../service/backend-api.service';

const SBPPaymentPage = ({ 
  amount, 
  amountUSD,
  steamAccount, 
  isCryptoPayment = false,
  cryptoAmount = 0,
  onPaymentSuccess, 
  onPaymentFail,
  onBack 
}) => {
  // Рассчитываем сумму в рублях для отображения
  // amount - это рубли с фронтенда, amountUSD - доллары
  const displayRubAmount = amount; // Используем рубли с фронтенда
  const [qrCode, setQrCode] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('PENDING');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(300); // 5 минут для пользователя
  const [webhookReceived, setWebhookReceived] = useState(false);
  
  // Состояние для прогресса создания QR-кода
  const [qrProgress, setQrProgress] = useState(0);
  const [qrMessage, setQrMessage] = useState('');
  const [paymentInitialized, setPaymentInitialized] = useState(false);
  const [paymentCreationStarted, setPaymentCreationStarted] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderIdCopied, setOrderIdCopied] = useState(false);
  const [paymentUrlCopied, setPaymentUrlCopied] = useState(false);
  const [showPaymentInterface, setShowPaymentInterface] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [cryptoCommissionPercentage, setCryptoCommissionPercentage] = useState(4); // Процент криптокомиссии

  // Загружаем криптокомиссию с бэкенда
  const loadCryptoCommission = async () => {
    try {
      const response = await backendApiService.getCryptoCommission();
      if (response.success) {
        console.log('✅ Криптокомиссия загружена в SBPPaymentPage:', response.commissionPercentage);
        setCryptoCommissionPercentage(response.commissionPercentage || 4);
      }
    } catch (error) {
      console.error('❌ Ошибка загрузки криптокомиссии:', error);
    }
  };

  useEffect(() => {
    // Создаем платеж только один раз
    if (!paymentInitialized && !paymentCreationStarted) {
      console.log('🔄 ИНИЦИАЛИЗАЦИЯ ПЛАТЕЖА:', {
        paymentInitialized: paymentInitialized,
        paymentCreationStarted: paymentCreationStarted,
        timestamp: new Date().toISOString()
      });
      setPaymentCreationStarted(true);
      initializePayment();
      setPaymentInitialized(true);
    } else {
      console.log('⚠️ ПЛАТЕЖ УЖЕ ИНИЦИАЛИЗИРОВАН ИЛИ В ПРОЦЕССЕ:', {
        paymentInitialized: paymentInitialized,
        paymentCreationStarted: paymentCreationStarted,
        timestamp: new Date().toISOString()
      });
    }
    
    // Таймер для пользователя (5 минут) - скрываем интерфейс оплаты
    const timerInterval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Время истекло - скрываем интерфейс оплаты, но продолжаем проверку
          console.log('⏰ Время на оплату истекло (5 минут), скрываем интерфейс, продолжаем проверку');
          setShowPaymentInterface(false);
          setCheckingPayment(true);
          setPaymentStatus('CHECKING');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Включаем EventSource для получения real-time уведомлений
    console.log('🔗 Включаем EventSource для получения уведомлений о платежах');
    const eventSource = startWebhookListening();

    return () => {
      clearInterval(timerInterval);
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [paymentInitialized, orderId]);

  // Загружаем криптокомиссию при монтировании компонента
  useEffect(() => {
    if (isCryptoPayment) {
      loadCryptoCommission();
    }
  }, [isCryptoPayment]);

  const startWebhookListening = () => {
    if (!orderId) {
      console.log('⚠️ orderId еще не получен, пропускаем настройку SSE');
      return null;
    }
    
    console.log('🔗 Настройка прослушивания SSE от бэкенда...');
    
    // Создаем EventSource для получения уведомлений от нашего сервера
    const eventSource = new EventSource(`${process.env.REACT_APP_BACKEND_URL}/api/sbp/webhook-stream/${orderId}`);
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 Получено SSE уведомление:', data);
        
        // Игнорируем ping сообщения
        if (data.type === 'ping') {
          return;
        }
        
        // Обрабатываем уведомления о статусе платежа
        if (data.type === 'payment_status') {
          if (data.status === 'SUCCESS') {
            setPaymentStatus('SUCCESS');
            setWebhookReceived(true);
            // НЕ закрываем соединение - ждем статус Steam
            console.log('✅ Платеж успешен, ждем пополнение Steam...');
          } else if (data.status === 'STEAM_SUCCESS') {
            setPaymentStatus('SUCCESS');
            setWebhookReceived(true);
            eventSource.close();
            console.log('🎮 Steam успешно пополнен!', data);
            onPaymentSuccess({
              ...data,
              steamAccount: data.steamAccount,
              amountUSD: data.amountUSD,
              message: data.message || 'Steam аккаунт успешно пополнен'
            });
          } else if (data.status === 'STEAM_FAILED') {
            setPaymentStatus('FAILED');
            setWebhookReceived(true);
            eventSource.close();
            console.error('❌ Ошибка пополнения Steam:', data);
            onPaymentFail(`Ошибка пополнения Steam: ${data.error || 'Неизвестная ошибка'}`);
          } else if (data.status === 'FAILED') {
            setPaymentStatus('FAILED');
            setWebhookReceived(true);
            eventSource.close();
            onPaymentFail('Платеж не прошел');
          } else if (data.status === 'CANCELLED') {
            setPaymentStatus('CANCELLED');
            setWebhookReceived(true);
            eventSource.close();
            onPaymentFail('Платеж отменен');
          }
        }
      } catch (error) {
        console.error('❌ Ошибка обработки SSE уведомления:', error);
      }
    };
    
    eventSource.onerror = (error) => {
      console.error('❌ Ошибка EventSource:', error);
      // Запускаем fallback polling при ошибке SSE
      console.log('⚠️ SSE недоступен, запускаем fallback polling');
      startPollingFallback();
    };
    
    // Сохраняем ссылку для закрытия
    return eventSource;
  };

  const startPollingFallback = () => {
    console.log('🔄 Запуск fallback polling (webhook недоступен)...');
    let attempts = 0;
    const maxAttempts = 360; // 30 минут (360 попыток по 5 секунд)
    
    const statusInterval = setInterval(async () => {
      attempts++;
      
      // Логируем только каждые 10 попыток, чтобы не засорять консоль
      if (attempts % 10 === 0) {
        console.log(`📡 Polling попытка ${attempts}/${maxAttempts} (${Math.round(attempts * 5 / 60)} минут)`);
      }
      
      if (attempts >= maxAttempts) {
        console.log('⏰ Polling остановлен - достигнут лимит попыток (30 минут)');
        clearInterval(statusInterval);
        setPaymentStatus('NOT_PAID');
        setCheckingPayment(false);
        onPaymentFail('Вы не оплатили заказ. При вопросах пишите в техподдержку t.me/tp_trendy_eng_shop');
        return;
      }
      
      try {
        const result = await sbpService.checkPaymentStatus(orderId);
        
        if (result.success && result.status === 'SUCCESS') {
          clearInterval(statusInterval);
          setPaymentStatus('SUCCESS');
          setCheckingPayment(false);
          await topUpSteam();
          onPaymentSuccess(result);
        } else if (result.success && result.status === 'FAILED') {
          clearInterval(statusInterval);
          setPaymentStatus('FAILED');
          setCheckingPayment(false);
          onPaymentFail('Платеж не прошел');
        }
      } catch (error) {
        console.error('❌ Ошибка fallback polling:', error);
        
        // Если это ошибка 400 (Bad Request), возможно платеж уже завершен
        // но API больше не доступен - проверяем через SSE или останавливаем polling
        if (error.message && error.message.includes('400')) {
          console.log('⚠️ API вернул 400 - возможно платеж уже завершен, останавливаем polling');
          clearInterval(statusInterval);
          setPaymentStatus('CHECKING');
          setCheckingPayment(false);
          // Не вызываем onPaymentFail, так как платеж мог быть успешным
          return;
        }
        
        // Для других ошибок продолжаем попытки
        console.log('🔄 Продолжаем polling после ошибки...');
      }
    }, 5000); // Проверяем каждые 5 секунд
  };

  const initializePayment = async () => {
    try {
      console.log('🚀 НАЧАЛО ИНИЦИАЛИЗАЦИИ ПЛАТЕЖА:', {
        steamAccount: steamAccount,
        amountUSD: amountUSD,
        amount: amount,
        timestamp: new Date().toISOString()
      });
      
      setIsLoading(true);
      
      // Функция обратного вызова для прогресса
      const onProgress = (progress, message) => {
        setQrProgress(progress);
        setQrMessage(message);
      };
      
      // Выбираем API в зависимости от типа оплаты
      let result;
      if (isCryptoPayment) {
        console.log('КРИПТОПЛАТЕЖ: ВЫЗОВ createCryptoPayment...');
        result = await sbpService.createCryptoPayment(
          steamAccount, 
          amountUSD,
          onProgress
        );
      } else {
        console.log('📞 ВЫЗОВ createPaymentWithTracking...');
        result = await sbpService.createPaymentWithTracking(
          steamAccount, 
          amountUSD, // Передаем доллары в API
          'USD',
          onProgress
        );
      }
      
      if (result.success) {
        if (isCryptoPayment) {
          // Для криптоплатежей просто устанавливаем URL оплаты
          setPaymentUrl(result.paymentUrl);
          console.log('🔗 Криптоплатеж создан, URL оплаты:', result.paymentUrl);
        } else {
          setQrCode(result.qrCode);
          setPaymentUrl(result.paymentUrl);
        }
        
        // Сохраняем orderId из результата (генерируется на бэкенде)
        if (result.orderId) {
          setOrderId(result.orderId);
          console.log('🆔 Получен orderId с бэкенда:', result.orderId);
        }
        
        console.log(`✅ ${isCryptoPayment ? 'Криптоплатеж' : 'Платеж'} создан с автоматическим отслеживанием`);
        console.log(`⏱️ Время создания: ${result.delay}с`);
      } else {
        setError(`Ошибка создания ${isCryptoPayment ? 'криптоплатежа' : 'QR-кода'}`);
      }
    } catch (error) {
      setError('Ошибка инициализации платежа');
    } finally {
      setIsLoading(false);
      setQrProgress(100);
      setQrMessage('QR-код готов');
    }
  };

  const topUpSteam = async () => {
    try {
      console.log('🎮 Пополнение Steam аккаунта происходит автоматически на бэкенде...');
      console.log(`💰 Сумма в USD для пополнения: ${amountUSD} (изначальная сумма)`);
      console.log(`💳 Сумма к оплате: ${amount} ₽ (с комиссией)`);
      console.log('✅ Пополнение Steam запущено автоматически через webhook');
    } catch (error) {
      console.error('❌ Ошибка пополнения Steam:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleManualPayment = () => {
    if (paymentUrl) {
      window.open(paymentUrl, '_blank');
    }
  };

  const copyOrderId = async () => {
    if (orderId) {
      try {
        await navigator.clipboard.writeText(orderId);
        setOrderIdCopied(true);
        setTimeout(() => setOrderIdCopied(false), 2000); // Сбрасываем через 2 секунды
      } catch (error) {
        console.error('Ошибка копирования:', error);
        // Fallback для старых браузеров
        const textArea = document.createElement('textarea');
        textArea.value = orderId;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setOrderIdCopied(true);
        setTimeout(() => setOrderIdCopied(false), 2000);
      }
    }
  };

  const copyPaymentUrl = async () => {
    if (paymentUrl) {
      try {
        await navigator.clipboard.writeText(paymentUrl);
        setPaymentUrlCopied(true);
        setTimeout(() => setPaymentUrlCopied(false), 2000);
      } catch (error) {
        console.error('Ошибка копирования URL:', error);
        const textArea = document.createElement('textarea');
        textArea.value = paymentUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setPaymentUrlCopied(true);
        setTimeout(() => setPaymentUrlCopied(false), 2000);
      }
    }
  };

  // Компонент прогресса создания платежа
  const PaymentProgressBar = () => (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {isCryptoPayment ? 'Создание криптоплатежа' : 'Создание QR-кода'}
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {qrProgress}%
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
        <div 
          className={`h-2 rounded-full transition-all duration-300 ease-out ${
            isCryptoPayment ? 'bg-purple-600' : 'bg-blue-600'
          }`}
          style={{ width: `${qrProgress}%` }}
        ></div>
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
        {qrMessage}
      </p>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4 ${
              isCryptoPayment ? 'border-purple-600' : 'border-blue-600'
            }`}></div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {isCryptoPayment ? 'Создание криптоплатежа' : 'Создание QR-кода для оплаты'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {isCryptoPayment ? 'Подключение к Heleket API...' : 'Это может занять до 10 секунд'}
            </p>
            
            {/* Прогресс-бар */}
            <PaymentProgressBar />
            
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {isCryptoPayment ? (
                <>
                  <p>⏳ Подключение к Heleket API...</p>
                  <p>🔒 Проверка подписи запроса</p>
                  <p>💳 Создание криптоадреса</p>
                </>
              ) : (
                <>
                  <p>⏳ СБП обрабатывает запрос...</p>
                  <p>🔒 Проверяется безопасность</p>
                  <p>📱 Генерируется QR-код</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Ошибка оплаты
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error}
            </p>
            <button
              onClick={onBack}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors"
            >
              Вернуться назад
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md w-full">
        <div className="text-center">
          {/* Заголовок */}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {isCryptoPayment ? 'Оплата криптовалютой' : 'Оплата через СБП'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Сумма к оплате: <span className={`font-bold ${isCryptoPayment 
              ? 'text-purple-600 dark:text-purple-400' 
              : 'text-green-600 dark:text-green-400'
            }`}>
              {isCryptoPayment ? `${cryptoAmount} USDT` : `${displayRubAmount} ₽`}
            </span>
          </p>

          {/* Информация о заказе */}
          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-6">
            <div className="space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">Steam аккаунт:</span> {steamAccount}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">Получите в Steam:</span> <span className="font-bold text-blue-600">${amountUSD}</span>
              </p>
              <div className="border-t pt-2 mt-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Заказ №:</span>
                  </p>
                  {orderId && (
                    <button
                      onClick={copyOrderId}
                      className={`text-xs px-2 py-1 rounded transition-colors ${
                        orderIdCopied 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800'
                      }`}
                    >
                      {orderIdCopied ? '✓ Скопировано' : '📋 Копировать'}
                    </button>
                  )}
                </div>
                <div className="mt-1">
                  <span className="font-mono text-xs bg-yellow-100 dark:bg-yellow-900 px-2 py-1 rounded block text-center">
                    {orderId || 'Генерация...'}
                  </span>
                </div>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 text-center">
                  ⚠️ На всякий случай сохраните номер заказа
                </p>
              </div>
            </div>
          </div>

          {/* QR-код и интерфейс оплаты - показываем только если время не истекло */}
          {showPaymentInterface && (
            <>
              {/* QR-код или адрес криптооплаты */}
              <div className="mb-6">
                {isCryptoPayment ? (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-6">
                    <div className="text-center">
                      <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
                        Оплата в USDT
                      </h3>
                      <div className="bg-white dark:bg-gray-800 p-3 rounded border">
                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                          Сумма к оплате:
                        </div>
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                          {cryptoAmount} USDT
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          (${amountUSD} + {cryptoCommissionPercentage}% комиссия)
                        </div>
                      </div>
                      {/* Простая ссылка на оплату */}
                      {paymentUrl ? (
                        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                          <div className="mb-4">
                            <div className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
                              💳 Оплата USDT
                            </div>
                            <div className="text-sm text-blue-700 dark:text-blue-300">
                              Сумма: <span className="font-mono font-bold">{cryptoAmount} USDT</span>
                            </div>
                            <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                              Выберите удобную сеть для оплаты
                            </div>
                          </div>
                          
                          <a
                            href={paymentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white text-lg font-medium rounded-lg transition-colors shadow-lg"
                          >
                            🌐 Перейти к оплате
                          </a>
                          
                          <div 
                            onClick={copyPaymentUrl}
                            className={`mt-3 text-xs break-all cursor-pointer transition-colors ${
                              paymentUrlCopied 
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                          >
                            {paymentUrlCopied ? '✓ URL скопирован!' : '📋 Скопировать ссылку'}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-800 dark:text-red-200">
                          ⚠️ URL оплаты не получен
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {qrCode && qrCode !== 'demo_qr_code' && qrCode !== 'existing_order' ? (
                      <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
                        <img 
                          src={`data:image/png;base64,${qrCode}`} 
                          alt="QR-код для оплаты" 
                          className="w-48 h-48"
                        />
                      </div>
                    ) : qrCode === 'existing_order' ? (
                      <div className="bg-yellow-100 p-4 rounded-lg border-2 border-yellow-300 inline-block">
                        <div className="w-48 h-48 bg-yellow-200 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-yellow-600 text-4xl mb-2">⚠️</div>
                            <span className="text-yellow-700 text-sm font-medium">Заказ уже существует</span>
                            <br />
                            <span className="text-yellow-600 text-xs">Ожидание оплаты...</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
                        <div className="w-48 h-48 bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-500 text-sm">Демо QR-код</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Инструкции */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  Как оплатить:
                </h3>
          {isCryptoPayment ? (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Инструкция по оплате USDT
              </h3>
              <ol className="text-sm text-gray-600 dark:text-gray-400 text-left space-y-2">
                <li className="flex items-start">
                  <span className="font-bold text-purple-600 mr-2">1.</span>
                  <span>Откройте ваш криптокошелек (Trust Wallet, MetaMask, Binance и др.)</span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-purple-600 mr-2">2.</span>
                  <span>Выберите USDT (Tether) в сети TRON</span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-purple-600 mr-2">3.</span>
                  <span>Отправьте <strong className="text-purple-600">{cryptoAmount} USDT</strong> на указанный адрес</span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-purple-600 mr-2">4.</span>
                  <span>Дождитесь подтверждения транзакции (обычно 1-3 минуты)</span>
                </li>
                <li className="flex items-start">
                  <span className="font-bold text-purple-600 mr-2">5.</span>
                  <span>Steam будет пополнен автоматически после подтверждения</span>
                </li>
              </ol>
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded text-sm text-blue-700 dark:text-blue-300">
                💡 <strong>Важно:</strong> Отправляйте точно {cryptoAmount} USDT. Небольшие отклонения могут привести к задержке обработки платежа.
              </div>
            </div>
          ) : (
                  <ol className="text-sm text-gray-600 dark:text-gray-400 text-left space-y-1">
                    <li>1. Откройте приложение вашего банка</li>
                    <li>2. Найдите раздел "СБП" или "QR-код"</li>
                    <li>3. Отсканируйте QR-код</li>
                    <li>4. Подтвердите оплату</li>
                  </ol>
                )}
              </div>

              {/* Альтернативная кнопка */}
              {!isCryptoPayment && (
                <button
                  onClick={handleManualPayment}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors mb-4 w-full"
                >
                  Открыть страницу оплаты
                </button>
              )}
            </>
          )}

          {/* Сообщение о проверке оплаты */}
          {checkingPayment && (
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg border border-blue-200 dark:border-blue-700">
              <div className="text-center">
                <div className="text-blue-600 text-4xl mb-2">⏳</div>
                <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                  Время на оплату истекло
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Если вы успели сделать платеж, мы еще проверяем оплату...
                </p>
                <div className="mt-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              </div>
            </div>
          )}

          {/* Таймер */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {timeLeft > 0 ? (
                <>Время на оплату: <span className="font-mono font-bold text-red-600">{formatTime(timeLeft)}</span></>
              ) : checkingPayment ? (
                <>⏰ Время на оплату истекло, но мы еще проверяем оплату...</>
              ) : paymentStatus === 'NOT_PAID' ? (
                <>❌ Вы не оплатили заказ. При вопросах пишите в техподдержку</>
              ) : (
                <>⏰ Время на оплату истекло, но мы продолжаем проверку статуса платежа</>
              )}
            </p>
          </div>

          {/* Статус */}
          <div className="mb-6">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              paymentStatus === 'SUCCESS' ? 'bg-green-100 text-green-800' :
              paymentStatus === 'FAILED' ? 'bg-red-100 text-red-800' :
              paymentStatus === 'CHECKING' ? 'bg-blue-100 text-blue-800' :
              paymentStatus === 'NOT_PAID' ? 'bg-red-100 text-red-800' :
              'bg-yellow-100 text-yellow-800'
            }`}>
              {paymentStatus === 'SUCCESS' && '✅ Оплачено'}
              {paymentStatus === 'FAILED' && '❌ Ошибка'}
              {paymentStatus === 'CHECKING' && '🔍 Проверяем оплату'}
              {paymentStatus === 'NOT_PAID' && '❌ Не оплачено'}
              {paymentStatus === 'PENDING' && '⏳ Ожидание оплаты'}
            </div>
            
            {/* Индикатор webhook */}
            {webhookReceived && (
              <div className="mt-2 text-xs text-green-600">
                🔔 Получено автоматическое уведомление
              </div>
            )}
          </div>

          {/* Кнопка назад */}
          <button
            onClick={onBack}
            className={`${isCryptoPayment 
              ? 'bg-purple-500 hover:bg-purple-600' 
              : 'bg-gray-500 hover:bg-gray-600'
            } text-white font-bold py-2 px-6 rounded-lg transition-colors`}
          >
            Отменить
          </button>
        </div>
      </div>
    </div>
  );
};

export default SBPPaymentPage;
